import { onMounted, onUnmounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { observeApiActivity } from '@/api/activity';
import { useAuthStore } from '@/stores/auth';
import { useMascotStore } from '@/stores/mascot';
import { activeWorkElapsed, PET_WORK_ROUTES, preparePetWork } from './petWork';

export function usePetProgress(): void {
  const route = useRoute(), mascot = useMascotStore(), auth = useAuthStore();
  let lastInput = 0, lastTick = Date.now();
  let timer: ReturnType<typeof setInterval> | undefined;
  const active = () => mascot.available && mascot.enabled && !document.hidden && document.hasFocus() && PET_WORK_ROUTES.has(String(route.name));
  const stop = observeApiActivity((path, init) => {
    if (!active()) return;
    const identify = preparePetWork(path, init), owner = auth.user?.id;
    if (!identify) return;
    return response => {
      if (auth.user?.id !== owner || !mascot.enabled || !mascot.available) return;
      const key = identify(response);
      if (key) mascot.recordWork(key);
    };
  });
  function input(event: Event): void { if (event.isTrusted && active()) lastInput = Date.now(); }
  function tick(): void {
    const now = Date.now(), elapsed = activeWorkElapsed(now, lastTick, lastInput, active());
    lastTick = now;
    if (elapsed) mascot.recordWorkTime(elapsed);
    else if (!document.hidden && route.name === 'pet') mascot.refreshProgress();
  }
  function reset(): void { lastTick = Date.now(); lastInput = 0; }
  watch(() => [auth.user?.id, route.name, mascot.enabled], reset, { flush: 'sync' });
  onMounted(() => {
    timer = setInterval(tick, 15_000);
    document.addEventListener('pointerdown', input, { passive: true, capture: true });
    document.addEventListener('keydown', input, { capture: true });
    document.addEventListener('scroll', input, { passive: true, capture: true });
    document.addEventListener('visibilitychange', reset);
    window.addEventListener('blur', reset);
  });
  onUnmounted(() => {
    stop(); clearInterval(timer);
    document.removeEventListener('pointerdown', input, true);
    document.removeEventListener('keydown', input, true);
    document.removeEventListener('scroll', input, true);
    document.removeEventListener('visibilitychange', reset);
    window.removeEventListener('blur', reset);
  });
}
